import React, { ReactElement, useEffect, useState } from "react";
import useLocales from "../../../hooks/useLocales";
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";
import { NoRowsOverlay } from "./NoRowsOverlay";
import CustomPagination from "./CustomPagination";
import Toolbar from "./Toolbar";
import { LinearProgress, styled } from "@mui/material";
import {
  DataGrid,
  trTR,
  enUS,
  GridEditMode,
  GridRowModesModel,
  GridRowParams,
  GridValidRowModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { useSearchDebounce } from "../../../hooks";
import { useLocation, useNavigate } from "react-router-dom";

const styleDatagrid = {
  boxShadow: "none",
  border: "0",
  fontSize: 16,
  padding: "6px 12px",
  lineHeight: 1.5,
  position: "relative",
  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
    py: 1,
  },
  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
    py: "15px",
  },
  "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
    py: "22px",
  },
  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
    width: "0.1em",
    height: "0.5em",
  },
  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
    background: "rgb(0, 0, 0,  0.10)",
    borderRadius: "100px",
  },
  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
    backgroundColor: "rgb(0, 0, 0,  0.20)",
    borderRadius: "100px",
  },
  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
};

interface IHideToolbars {
  create?: boolean;
  export?: boolean;
  dense?: boolean;
  columnVisibility?: boolean;
  filter?: boolean;
  advancedFilter?: boolean;
  customFilter?: boolean;
  search?: boolean;
  defaultOpenFilter?: boolean;
  showActions?: boolean;
  excel?: boolean;
  supplier?: boolean;
}

export const defaultToolbar = {
  create: true,
  export: false,
  dense: true,
  columnVisibility: true,
  filter: true,
  advancedFilter: false,
  customerFilter: false,
  search: false,
  defaultOpenFilter: false,
  showActions: true,
  excel: false,
  supplier: false,
};

interface IMuiDatagridProps {
  rows: any[];
  columns: any[];
  path?: string;
  createBtnClickHandler?: any;
  hideToolbars?: IHideToolbars;
  filters?: ReactElement;
  advancedFilter?: ReactElement;
  customFilter?: ReactElement;
  initialState?: GridInitialStateCommunity;
  loading: boolean;
  rowCount: number;
  pageSize: number;
  pageNumber: number;
  setPagination: any;
  checkboxSelection?: boolean;
  selectionModel?: any;
  keepNonExistentRowsSelected?: boolean;
  isRowSelectable?: (params: GridRowParams<GridValidRowModel>) => boolean;
  onSelectionModelChange?: (model: any) => void;
  columnVisibilityModel?: any;
  onColumnVisibilityModelChange?: (model: any) => void;
  customExport?: JSX.Element;
  rowTooltip?: any;
  getRowClassName?: (params: any) => string;
  enchancedToolbar?: ReactElement;
  excelButtons?: ReactElement;
  supplierButton?: ReactElement;
  getRowId?: any;
  disableSelectionOnClick?: boolean;
  editMode?: GridEditMode;
  processRowUpdate?: any;
  rowModesModel?: GridRowModesModel;
  rowModesModelChangeHandler?: (newRowModesModel: GridRowModesModel) => void;
  hideFooter?: boolean;
  sss?: boolean; // server-side sorting
  autoHeight?: any;
}

const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: 20,
  width: 20,
  height: 20,
  border: "2px solid #CCC",
  backgroundColor: theme.palette.mode === "dark" ? "#394b59" : "transparent",
  "&:before": {
    display: "block",
    width: 16,
    height: 16,
    padding: "2px",
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%FFF'/%3E%3C/svg%3E\")",
    content: '""',
  },
  "&.Mui-disabled": {
    color: theme
      ? "rgba(255, 255, 255, 0.38) !important"
      : "rgba(0, 0, 0, 0.38)",
  },
  "input:hover ~ &": {
    backgroundColor: theme.palette.mode === "dark" ? "#30404d" : "transparent",
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    "&:before": {
      display: "block",
      width: 16,
      height: 16,
      padding: "2px",
      backgroundImage: "none",
      content: '""',
    },
    background:
      theme.palette.mode === "dark"
        ? "rgba(57,75,89,.5)"
        : "rgba(206,217,224,.5)",
  },

  "input:disabled:checked ~ &": {
    boxShadow: "none",
    background: "rgb(133 62 248 / 38%)",
    "&:before": {
      display: "block",
      width: 16,
      height: 16,
      padding: "2px",
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
        " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
        "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
      content: '""',
    },
  },
}));

const BpCheckedIcon = styled(BpIcon)(({ theme }: any) => ({
  backgroundColor: "#853EF8",
  border: "2px solid transparent",
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
  "&:before": {
    display: "block",
    width: 16,
    height: 16,

    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  "input:hover ~ &": {
    backgroundColor: "#853EF8",
  },
  "MuiFormControlLabel-label.Mui-disabled": {
    color:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.38) !important"
        : "rgba(0, 0, 0, 0.38)",
  },
}));

const deleteSorting = (filter: any) => {
  const newFilter = { ...filter };
  delete newFilter.sorting;
  return newFilter;
};

export default function MuiDatagrid({
  rows,
  columns,
  path,
  createBtnClickHandler,
  hideToolbars,
  filters,
  advancedFilter,
  customFilter,
  initialState,
  loading,
  rowCount,
  pageSize,
  pageNumber,
  setPagination,
  columnVisibilityModel,
  onSelectionModelChange,
  selectionModel,
  onColumnVisibilityModelChange,
  customExport,
  keepNonExistentRowsSelected,
  checkboxSelection,
  disableSelectionOnClick,
  isRowSelectable,
  enchancedToolbar,
  excelButtons,
  supplierButton,
  getRowClassName,
  getRowId = "id",
  editMode = "cell",
  processRowUpdate,
  rowModesModel,
  rowModesModelChangeHandler,
  hideFooter = false,
  sss = false,
  autoHeight,
}: IMuiDatagridProps) {
  const { currentLang } = useLocales();
  const locale = currentLang.value === "tr" ? trTR : enUS;
  const localeText = locale.components.MuiDataGrid.defaultProps.localeText;
  const [searchText, setSearchText] = useState<string>("");
  const [searchQuery, setSearchQuery] = useSearchDebounce();
  const [rowsData, setRowsData] = useState<any[]>(rows);
  const navigate = useNavigate();
  let { pathname } = useLocation();

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (!sss) return;
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        setPagination((prev: any) => ({
          ...prev,
          params: {
            ...prev.params,
            filter: { ...prev.params.filter, sorting: `${field}_${sort}` },
          },
        }));
        navigate({
          pathname: pathname,
          search: `?sorting=${field}_${sort}`,
        });
      } else {
        setPagination((prev: any) => ({
          ...prev,
          params: {
            ...prev.params,
            filter: { ...deleteSorting(prev.params.filter) },
          },
        }));
        navigate({
          pathname: pathname,
        });
      }
    },
    []
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filterButtonEl, setFilterButtonEl] =
    useState<HTMLButtonElement | null>(null);

  const [columnButtonEl, setColumnButtonEl] =
    useState<HTMLButtonElement | null>(null);

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue);
    setSearchQuery(searchValue);
  };

  useEffect(() => {
    if (setPagination) {
      setPagination((prev: any) => ({
        ...prev,
        params: {
          ...prev.params,
          filter: {
            ...prev.params.filter,
          },
        },
      }));
    }

    if (searchQuery) {
      setPagination((prev: any) => ({
        ...prev,
        params: {
          ...prev.params,
          filter: {
            ...prev.params.filter,
            usedName: searchQuery,
          },
        },
      }));
    }

    if (searchQuery === "") {
      setPagination((prev: any) => {
        const { filter } = prev.params;

        if ("usedName" in filter) {
          delete filter.usedName;
        }

        return {
          ...prev,
        };
      });
    }
  }, [searchQuery, setPagination]);

  useEffect(() => {
    setRowsData(rows);
  }, [rows]);

  return (
    <>
      <DataGrid
        sortingMode={sss ? "server" : "client"}
        disableColumnMenu={sss}
        onSortModelChange={handleSortModelChange}
        getRowClassName={getRowClassName}
        keepNonExistentRowsSelected={keepNonExistentRowsSelected}
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        checkboxSelection={checkboxSelection}
        isRowSelectable={isRowSelectable}
        sx={styleDatagrid}
        initialState={initialState}
        rowCount={rowCount ? rowCount : 10}
        rowsPerPageOptions={[5, 10, 20]}
        pagination
        page={pageNumber}
        pageSize={pageSize}
        paginationMode="server"
        loading={loading}
        rows={rowsData}
        columns={columns}
        disableSelectionOnClick={
          keepNonExistentRowsSelected
            ? true
            : disableSelectionOnClick
            ? true
            : false
        }
        localeText={localeText}
        autoHeight
        editMode={editMode}
        processRowUpdate={(newRow: any, oldRow: any) =>
          processRowUpdate && processRowUpdate(newRow, oldRow)
        }
        rowModesModel={rowModesModel}
        onRowModesModelChange={(newRowModesModel: GridRowModesModel) =>
          rowModesModelChangeHandler &&
          rowModesModelChangeHandler(newRowModesModel)
        }
        experimentalFeatures={{ newEditingApi: true }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        /* getRowHeight={() => "auto"}
        getEstimatedRowHeight={() => 10} */
        getRowId={(row) => (getRowId ? row[getRowId] : row.id)}
        onPageChange={(newPage) => {
          setPagination((prev: any) => ({
            ...prev,
            params: {
              ...prev.params,
              pagination: { ...prev.params.pagination, pageNumber: newPage },
            },
          }));
        }}
        onPageSizeChange={(newPageSize) =>
          setPagination((prev: any) => ({
            ...prev,
            params: {
              ...prev.params,
              pagination: { ...prev.params.pagination, size: newPageSize },
            },
          }))
        }
        components={{
          Toolbar: Toolbar,
          Pagination: CustomPagination,
          LoadingOverlay: LinearProgress,
          NoRowsOverlay: NoRowsOverlay,
          //BaseCheckbox: CheckboxWrapper,
        }}
        hideFooter={hideFooter}
        componentsProps={{
          toolbar: {
            value: searchText,
            onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
              requestSearch(event.target.value),
            clearSearch: () => requestSearch(""),
            path: path,
            setFilterButtonEl,
            setColumnButtonEl,
            hideToolbars: hideToolbars ? hideToolbars : defaultToolbar,
            filters,
            advancedFilter,
            customFilter,
            customExport: customExport,
            enchancedToolbar: enchancedToolbar,
            createBtnClickHandler,
            excelButtons: excelButtons,
            supplierButton: supplierButton,
          },
          panel: {
            anchorEl: columnButtonEl,
          },
        }}
      />
    </>
  );
}
